import { StarRating } from './star-rating'
import type { ActionPlanReflection } from '@/types'
import { canDeleteReflection } from '../../functions-src/lib/action-plan-access'

interface ReflectionCardProps {
  reflection: ActionPlanReflection
  currentUserId?: string
  isAdmin?: boolean
  onEdit: () => void
  onDelete: () => void
}

export function ReflectionCard({ reflection, currentUserId, isAdmin, onEdit, onDelete }: ReflectionCardProps) {
  const canEdit = currentUserId === reflection.user_id
  // isAdmin is the only role signal passed in; anyone on this page is at least a viewer
  const canDelete = canDeleteReflection(isAdmin ? 'admin' : 'viewer', currentUserId, reflection.user_id)

  return (
    <div className="bg-cfa-light-gray/50 rounded-lg p-3 space-y-2">
      {/* User + Rating */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {reflection.user_avatar ? (
            <img src={reflection.user_avatar} alt="" className="w-6 h-6 rounded-full" />
          ) : (
            <div className="w-6 h-6 rounded-full bg-brand-blue/20 flex items-center justify-center text-xs font-medium text-brand-blue">
              {reflection.user_name?.[0]?.toUpperCase()}
            </div>
          )}
          <span className="text-sm font-medium text-cfa-dark-gray">{reflection.user_name}</span>
        </div>
        <StarRating value={reflection.effectiveness_rating} readonly size="sm" />
      </div>

      {/* Text sections */}
      {reflection.what_worked && (
        <div className="text-sm">
          <span className="font-medium text-success-600">Worked: </span>
          <span className="text-cfa-dark-gray/80">{reflection.what_worked}</span>
        </div>
      )}
      {reflection.what_didnt_work && (
        <div className="text-sm">
          <span className="font-medium text-danger-500">Didn't work: </span>
          <span className="text-cfa-dark-gray/80">{reflection.what_didnt_work}</span>
        </div>
      )}
      {reflection.general_thoughts && (
        <div className="text-sm">
          <span className="font-medium text-cfa-dark-gray/70">Thoughts: </span>
          <span className="text-cfa-dark-gray/80">{reflection.general_thoughts}</span>
        </div>
      )}

      {/* Actions */}
      {(canEdit || canDelete) && (
        <div className="flex gap-2 pt-1">
          {canEdit && (
            <button onClick={onEdit} className="text-xs text-brand-blue hover:text-brand-blue/80">Edit</button>
          )}
          {canDelete && (
            <button onClick={onDelete} className="text-xs text-danger-500 hover:text-danger-700">Delete</button>
          )}
        </div>
      )}
    </div>
  )
}
